/**
 * @module modal-video-loader
 * Handles the logic for opening a video in a MicroModal popup.
 * It dynamically creates the video player based on data attributes.
 */

/**
 * Creates the appropriate video player HTML (iframe or <video>) based on ACF data.
 * @param {object} videoData - The video data object from ACF.
 * @returns {string} The HTML string for the video player.
 */
function createVideoPlayerHTML(videoData) {
    if (!videoData || !videoData.hosting || videoData.hosting === 'none') {
        return '';
    }

    const { hosting } = videoData;
    let playerHTML = '';
    // Default to a 16:9 aspect ratio if we can't determine it.
    let aspectRatioPercent = (9 / 16) * 100;

    switch (hosting) {
        case 'local':
            if (videoData.files && videoData.files.length > 0) {
                const firstFile = videoData.files[0].file;
                if (firstFile && firstFile.width && firstFile.height && firstFile.width > 0) {
                    aspectRatioPercent = (firstFile.height / firstFile.width) * 100;
                }

                const sources = videoData.files.map(fileRow => {
                    const file = fileRow.file;
                    if (file && file.url) {
                        const extension = file.url.split('.').pop().toLowerCase();
                        let mimeType = '';
                        if (extension === 'mp4') mimeType = 'video/mp4';
                        else if (extension === 'webm') mimeType = 'video/webm';
                        else if (extension === 'ogv') mimeType = 'video/ogg';
                        if (mimeType) {
                            return `<source src="${file.url}" type="${mimeType}">`;
                        }
                    }
                    return '';
                }).join('');
                // Add `controls` and `autoplay` for user interaction in the modal.
                // The responsive wrapper handles sizing, so `fill cover` classes are removed.
                playerHTML = `<video controls autoplay playsinline>${sources}</video>`;
            }
            break;

        case 'streaming':
            if (videoData.video_streaming) {
                // We can't know the aspect ratio of a streaming URL, so we use the default.
                playerHTML = `<video src="${videoData.video_streaming}" controls autoplay playsinline></video>`;
            }
            break;

        case 'embed':
            if (videoData.video_embed) {
                let embedCode = videoData.video_embed;

                // Try to parse width and height from the iframe to calculate aspect ratio.
                const widthMatch = embedCode.match(/width="(\d+)"/);
                const heightMatch = embedCode.match(/height="(\d+)"/);
                if (widthMatch && heightMatch && parseInt(widthMatch[1], 10) > 0) {
                    aspectRatioPercent = (parseInt(heightMatch[1], 10) / parseInt(widthMatch[1], 10)) * 100;
                }

                // Use regex to find the src attribute and add autoplay parameters
                if (embedCode.includes('<iframe')) {
                    const srcMatch = embedCode.match(/src="([^"]+)"/);
                    if (srcMatch && srcMatch[1]) {
                        try {
                            let url = new URL(srcMatch[1]);
                            // Add minimalist parameters for common video services
                            // We remove autoplay from the URL and rely on the `allow` attribute.
                            if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be') || url.hostname.includes('youtube-nocookie.com')) {
                                url.searchParams.set('autoplay', '1');
                                url.searchParams.set('mute', '0'); // Explicitly request sound
                                url.searchParams.set('controls', '1'); // Show basic controls
                                url.searchParams.set('rel', '0'); // No related videos from other channels
                                url.searchParams.set('showinfo', '0'); // Hide title, uploader (deprecated but good fallback)
                                url.searchParams.set('modestbranding', '1'); // Hide YouTube logo
                                url.searchParams.set('iv_load_policy', '3'); // Disable annotations
                                url.searchParams.set('enablejsapi', '1'); // Required for some interactions
                            } else if (url.hostname.includes('vimeo.com')) {
                                url.searchParams.set('autoplay', '1');
                                url.searchParams.set('muted', '0'); // Explicitly request sound
                                url.searchParams.set('controls', '1'); // Show basic controls
                                url.searchParams.set('title', '0'); // Hide title
                                url.searchParams.set('byline', '0'); // Hide byline
                                url.searchParams.set('portrait', '0'); // Hide user portrait
                                url.searchParams.set('badge', '0'); // Hide badge
                                url.searchParams.set('dnt', '1'); // Enable Do Not Track
                            }
                            embedCode = embedCode.replace(srcMatch[1], url.href);

                            // Also strip unwanted attributes from the iframe tag itself
                            embedCode = embedCode.replace(/ title="[^"]*"/i, '');
                            embedCode = embedCode.replace(/ allow="[^"]*"/i, ' allow="autoplay; fullscreen; picture-in-picture"');
                            embedCode = embedCode.replace(/ allowfullscreen/i, '');
                            embedCode = embedCode.replace(/ allow="[^"]*"/i, ' allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer"');
                            embedCode = embedCode.replace(/ allowfullscreen/i, ' allowfullscreen'); // Ensure it's present
                        } catch (error) {
                            console.error("Could not parse video embed URL:", error);
                        }
                    }
                }
                playerHTML = embedCode;
            }
            break;
    }

    if (!playerHTML) {
        return '';
    }

    // Wrap the player in a responsive container with the calculated aspect ratio.
    return `<div class="responsive-video-wrapper" style="padding-bottom: ${aspectRatioPercent}%;">${playerHTML}</div>`;
}

/**
 * Initializes the event listeners for the video popup modal.
 * Uses event delegation to handle dynamically added buttons.
 */
export function initializeVideoPopupHandler() {
    // Access window.MicroModal here, at the time of execution, not at module load time.
    // This ensures it has been initialized by app.js.
    if (typeof window.MicroModal === 'undefined') {
        console.error('MicroModal is not available. Video popup will not work. Ensure it is initialized on window before calling this handler.');
        return;
    }

    const modalId = 'video-modal';
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return; // No video modal on this page.

    const playerContainer = modalElement.querySelector('#video-modal-iframe');
    if (!playerContainer) return;

    document.body.addEventListener('click', (e) => {
        const button = e.target.closest('.media__video-popup-open');
        if (!button) return;

        e.preventDefault();

        try {
            const videoData = JSON.parse(button.dataset.videoData);
            playerContainer.innerHTML = createVideoPlayerHTML(videoData);
            window.MicroModal.show(modalId, { onClose: () => { playerContainer.innerHTML = ''; } });
        } catch (error) {
            console.error('Failed to parse video data or show modal:', error);
        }
    });
}